import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Lightbulb, Sparkles, Copy, Download, ChevronDown, ChevronUp } from 'lucide-react'
import { api } from '../../api/client'
import Button from '../../components/ui/Button'
import Input, { Select, Textarea } from '../../components/ui/Input'

const AGENT_OPTIONS = [
  { value: 'pm', label: 'Product Manager' },
  { value: 'marketing', label: 'Marketing Strategist' },
  { value: 'sales', label: 'Sales Strategist' },
]

const MODE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  pm: [
    { value: 'discovery', label: 'Discovery' },
    { value: 'research', label: 'Research' },
    { value: 'prd', label: 'PRD' },
  ],
  marketing: [
    { value: 'campaign', label: 'Campaign Plan' },
    { value: 'gtm', label: 'GTM Strategy' },
  ],
  sales: [
    { value: 'playbook', label: 'Sales Playbook' },
    { value: 'outreach', label: 'Outreach' },
    { value: 'deal', label: 'Deal Strategy' },
    { value: 'pricing', label: 'Pricing Analysis' },
  ],
}

const KNOWN_FIELDS = [
  'title', 'problem_statement', 'objective', 'rice_score',
  'functional_requirements', 'user_stories', 'target_users', 'risks', 'assumptions',
]

export default function StrategyPage() {
  const [agent, setAgent] = useState('pm')
  const [mode, setMode] = useState('discovery')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [audience, setAudience] = useState('')
  const [context, setContext] = useState('')
  const [showRaw, setShowRaw] = useState(false)

  const mutation = useMutation({
    mutationFn: (payload: Record<string, any>) => api.post<any>(`/${agent}`, payload),
  })

  const handleAgentChange = (newAgent: string) => {
    setAgent(newAgent)
    const modes = MODE_OPTIONS[newAgent]
    if (modes && modes.length > 0) {
      setMode(modes[0].value)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: Record<string, any> = { mode, title, description }
    if (audience) payload.audience = audience
    if (context) payload.context = context
    mutation.mutate(payload)
  }

  const copyResult = () => {
    if (mutation.data) navigator.clipboard.writeText(JSON.stringify(mutation.data, null, 2))
  }

  const downloadResult = () => {
    if (!mutation.data) return
    const blob = new Blob([JSON.stringify(mutation.data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `strategy_${agent}_${mode}_result.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const currentModes = MODE_OPTIONS[agent] || []

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Lightbulb size={20} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strategy & Planning</h1>
          <p className="text-sm text-gray-500">Product management, marketing strategy, and sales planning</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Select
              label="Agent"
              options={AGENT_OPTIONS}
              value={agent}
              onChange={(e) => handleAgentChange(e.target.value)}
            />
            <Select
              label="Mode"
              options={currentModes}
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            />
            <Input
              label="Title / Topic"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g. Storepecker mobile app redesign"
            />
            <Textarea
              label="Description / Objective"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              placeholder="What are you trying to achieve?"
            />
            <Input
              label="Target Audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. Indian SMB retailers"
            />
            <Textarea
              label="Additional Context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Business context, competitors, constraints..."
            />
            <Button type="submit" loading={mutation.isPending} className="w-full">
              <Sparkles size={16} /> Generate
            </Button>
            {mutation.error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                {(mutation.error as Error).message}
              </div>
            )}
          </form>
        </div>

        {/* Result */}
        <div>
          {mutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm">Generating... This may take 30-60 seconds.</p>
            </div>
          )}
          {mutation.data && !mutation.isPending && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={copyResult}>
                  <Copy size={14} /> Copy JSON
                </Button>
                <Button variant="ghost" size="sm" onClick={downloadResult}>
                  <Download size={14} /> Download
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowRaw(!showRaw)}>
                  {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showRaw ? 'Hide' : 'Show'} Raw
                </Button>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <StrategyResult data={mutation.data} />
              </div>

              {showRaw && (
                <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-auto max-h-96">
                  <pre className="text-xs font-mono whitespace-pre-wrap">
                    {JSON.stringify(mutation.data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          {!mutation.data && !mutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <Sparkles size={32} className="mb-3 opacity-30" />
              <p className="text-sm">Fill the form and click Generate</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StrategyResult({ data }: { data: any }) {
  const r = data?.result || data

  if (typeof r === 'string') {
    return (
      <div
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: r.replace(/\n/g, '<br/>') }}
      />
    )
  }

  const remainingEntries = Object.entries(r || {}).filter(
    ([k, v]) => !KNOWN_FIELDS.includes(k) && v != null && !(Array.isArray(v) && v.length === 0) && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0),
  )

  return (
    <div className="space-y-4">
      {r?.title && <h2 className="text-lg font-bold">{r.title}</h2>}
      {r?.problem_statement && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase">Problem</h4>
          <p className="text-sm">{r.problem_statement}</p>
        </div>
      )}
      {r?.objective && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase">Objective</h4>
          <p className="text-sm">{r.objective}</p>
        </div>
      )}
      {r?.rice_score != null && (
        <div className="bg-indigo-50 p-3 rounded-lg">
          <h4 className="text-xs font-medium text-indigo-700 uppercase mb-1">RICE Score</h4>
          <p className="text-xl font-bold text-indigo-700">
            {typeof r.rice_score === 'object' ? r.rice_score.score : r.rice_score}
          </p>
        </div>
      )}

      {['functional_requirements', 'user_stories', 'target_users', 'risks', 'assumptions'].map(key => {
        const items = r?.[key]
        if (!items?.length) return null
        return (
          <div key={key}>
            <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">
              {key.replace(/_/g, ' ')}
            </h4>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
              {items.map((item: any, i: number) => (
                <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
              ))}
            </ul>
          </div>
        )
      })}

      {remainingEntries.map(([key, value]) => (
        <div key={key}>
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">
            {key.replace(/_/g, ' ')}
          </h4>
          {typeof value === 'string' ? (
            <p className="text-sm text-gray-700">{value}</p>
          ) : Array.isArray(value) ? (
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
              {value.map((v: any, i: number) => (
                <li key={i}>{typeof v === 'string' ? v : JSON.stringify(v)}</li>
              ))}
            </ul>
          ) : (
            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto">
              {JSON.stringify(value, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  )
}
